import { AsyncPipe } from '@angular/common';
import { Component, inject, input, OnDestroy, OnInit, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { UserNamePipe } from 'src/app/pipes/user-name.pipe';
import { AuditService } from 'src/app/services/audit.service';
import { ExportService } from 'src/app/services/exportService';
import { FileUtilsService } from 'src/app/services/file-utils.service';

import { Audit } from '../../../models/audit/audit';
import { Templates } from '../../../models/templates';
import { AccessService } from '../../../services/access.service';
import { ExcelexportService } from '../../../services/excelexport.service';
import { KeepFilterService } from '../../../services/keep-filter.service';
import { AzureIdStatusPipe } from '../../../pipes/azure-id-status.pipe';

@Component({
  selector: 'app-audit-table',
  templateUrl: './audit-table.component.html',
  styleUrls: ['./audit-table.component.scss'],
  imports: [
    UserNamePipe,
    AsyncPipe,
    MatToolbarModule,
    MatButtonModule,
    RouterLink,
    MatProgressSpinnerModule,
    MatGridListModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatSelectModule,
    MatOptionModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    AzureIdStatusPipe
  ]
})
export class AuditTableComponent implements OnInit, OnDestroy {
  private auditService = inject(AuditService);
  private keepFilterService = inject(KeepFilterService);
  private route = inject(ActivatedRoute);
  readonly accessService = inject(AccessService);
  private excelExportService = inject(ExcelexportService);
  private exportService = inject(ExportService);
  private snackBar = inject(MatSnackBar);
  fileUtilService = inject(FileUtilsService);
  router = inject(Router);

  readonly all = input<boolean>(false);
  area!: string;
  lastArea!: string;

  isError = false;

  titleSelect = '';
  statusSelect = '';
  assessmentSelect = '';
  legalUnitSelect = '';
  bgSelect = '';
  plantSelect = '';
  reportYearSelect = '';

  possibleCoordinators: string[] = [];
  possibleLegalUnits: string[] = [];
  possibleBgs: string[] = [];
  possibleReportYears: string[] = [];
  possiblePlants: string[] = [];
  download = false;
  columns = [
    { title: 'Status', field: 'status' },
    { title: 'Audit Title', field: 'auditTitle', custom: 'split' },
    { title: 'Report Year', field: 'reportingYear' },
    {
      title: 'Auditee',
      field: 'assessmentCoordinator',
      custom: 'name'
    },
    { title: 'Plant', field: 'plant' },
    { title: 'Legal Unit', field: 'legalUnit' },
    { title: 'BG', field: 'department' }
  ];

  get columnFields() {
    return this.columns.map((v) => v.field);
  }

  dataSource!: MatTableDataSource<Audit>;
  unfilteredDataSource!: MatTableDataSource<Audit>;

  readonly paginator = viewChild.required(MatPaginator);
  readonly sort = viewChild.required(MatSort);

  ngOnInit() {
    this.route.params?.subscribe((param) => {
      this.area = param.area;
      this.getAudits();
    });
    this.accessService.initRoles();
    this.revertFilter();
  }

  isDownload(): boolean {
    return this.download;
  }

  ngOnDestroy() {
    this.keepFilter();
  }

  keepFilter() {
    this.keepFilterService.audit_assessmentCoordinator = this.assessmentSelect;
    this.keepFilterService.auditTitle = this.titleSelect;
    this.keepFilterService.audit_status = this.statusSelect;
    this.keepFilterService.audit_legalUnit = this.legalUnitSelect;
    this.keepFilterService.bg = this.bgSelect;
    this.keepFilterService.plant = this.plantSelect;
    this.keepFilterService.reportYear = this.reportYearSelect;
  }

  revertFilter() {
    this.assessmentSelect = this.keepFilterService.audit_assessmentCoordinator;
    this.titleSelect = this.keepFilterService.auditTitle;
    this.statusSelect = this.keepFilterService.audit_status;
    this.legalUnitSelect = this.keepFilterService.audit_legalUnit;
    this.bgSelect = this.keepFilterService.bg;
    this.plantSelect = this.keepFilterService.plant;
    this.reportYearSelect = this.keepFilterService.reportYear;
  }

  getAudits(): void {
    this.auditService.getData(this.area, this.all()).subscribe({
      next: (recs) => {
        this.dataSource = new MatTableDataSource(recs);
        this.unfilteredDataSource = new MatTableDataSource(recs);
        this.getPossibleKeywordValues();
        this.lastArea = this.route.snapshot.params.area;
        this.dataSource.paginator = this.paginator();
        this.dataSource.sort = this.sort();
        this.filterData();
      },
      error: () => {
        this.isError = true;
        this.snackBar.open('Could not get Audits', undefined, {
          panelClass: 'error',
          duration: 2000
        });
      }
    });
  }

  isDefined(): boolean {
    if (this.isError) {
      return true;
    }
    if (this.unfilteredDataSource) {
      if (this.unfilteredDataSource.data.length > 0) {
        if (this.lastArea === this.route.snapshot.params.area) {
          return true;
        }
      } else if (this.lastArea === this.route.snapshot.params.area) {
        return true;
      }
    }
    return false;
  }

  getPossibleKeywordValues() {
    this.possibleCoordinators = [];
    this.possibleLegalUnits = [];
    this.possibleBgs = [];
    this.possibleReportYears = [];
    this.possiblePlants = [];

    this.unfilteredDataSource.data.forEach((element) => {
      if (!this.possibleLegalUnits.includes(element.legalUnit) && element.legalUnit) {
        this.possibleLegalUnits.push(element.legalUnit);
      }
      const clearAssessmentCoordinatorName = element.assessmentCoordinatorName || element.assessmentCoordinator;

      if (clearAssessmentCoordinatorName && !this.possibleCoordinators.includes(clearAssessmentCoordinatorName)) {
        this.possibleCoordinators.push(clearAssessmentCoordinatorName);
      }
      if (this.possibleBgs.includes(element.department) === false && element.department) {
        this.possibleBgs.push(element.department);
      }
      if (this.possiblePlants.includes(element.plant) === false && element.plant) {
        this.possiblePlants.push(element.plant);
      }
      if (this.possibleReportYears.includes(element.reportingYear) === false && element.reportingYear) {
        this.possibleReportYears.push(element.reportingYear);
      }
    });

    const selection: string[] = [];
    this.possibleCoordinators.forEach((element) => {
      selection.push(element);
    });
    this.possibleCoordinators = selection;

    this.possibleLegalUnits.sort();
    this.possibleCoordinators.sort();
    this.possibleReportYears.sort();
    this.possiblePlants.sort();
  }

  showAll() {
    this.statusSelect = '';
    this.legalUnitSelect = '';
    this.assessmentSelect = '';
    this.titleSelect = '';
    this.bgSelect = '';
    this.plantSelect = '';
    this.reportYearSelect = '';
    this.dataSource.data = this.unfilteredDataSource.data;
  }

  filterData() {
    const data = this.unfilteredDataSource.data
      .filter((element) => {
        if (this.titleSelect && !element.auditTitle) {
          return false;
        }
        return element.auditTitle.toLowerCase().includes(this.titleSelect.toLowerCase()) || this.titleSelect === '';
      })
      .filter((element) => {
        return element.legalUnit?.includes(this.legalUnitSelect) || this.legalUnitSelect === '';
      })
      .filter((element) => {
        const assessmentCoordinatorName = element.assessmentCoordinatorName || element.assessmentCoordinator;
        return assessmentCoordinatorName?.includes(this.assessmentSelect) || this.assessmentSelect === '';
      })
      .filter((element) => {
        return element.status?.includes(this.statusSelect) || this.statusSelect === '';
      })
      .filter((element) => {
        return element.department?.includes(this.bgSelect) || this.bgSelect === '';
      })
      .filter((element) => {
        return element.plant?.includes(this.plantSelect) || this.plantSelect === '';
      })
      .filter((element) => {
        return element.reportingYear?.includes(this.reportYearSelect) || this.reportYearSelect === '';
      });
    this.dataSource.data = data;
  }

  exportExcel() {
    this.download = true;
    this.excelExportService
      .exportListAudit({ list: this.dataSource.data, template: Templates.AUDIT })
      .pipe()
      .subscribe((blob: Blob) => {
        this.exportService.createFileDownloadFromBlob(blob, 'Audit.xlsx');
        this.download = false;
      });
  }

  canCreate() {
    switch (this.area) {
      case 'GPRM':
        return this.accessService.isGprmAuditor();
      case 'GPIA':
        return this.accessService.isGpiaAuditor();
      default:
        return false;
    }
  }

  navigateToAudit(entry: Audit): void {
    this.router.navigateByUrl(`/audit_detail/${entry.id}`);
  }
}
